import React from 'react';
import { useTranslation } from 'react-i18next';
import '../../../styles/widgets/Widget.css';

const Widget = ({
    type = 'default',
    title,
    subtitle,
    children,
    className = '',
    size = 'medium',
    loading = false,
    error = null,
    onRetry,
    headerAction,
    ...props
}) => {
    const { t } = useTranslation('common');
    if (loading) {
        return (
            <div className={`widget widget--${type} widget--${size} widget--loading ${className}`} {...props}>
                {title && (
                    <div className="widget__header">
                        <div className="widget__title-skeleton"></div>
                    </div>
                )}
                <div className="widget__content">
                    <WidgetSkeleton type={type} />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`widget widget--${type} widget--${size} widget--error ${className}`} {...props}>
                {title && (
                    <div className="widget__header">
                        <h3 className="widget__title">{title}</h3>
                    </div>
                )}
                <div className="widget__content">
                    <div className="widget-error">
                        <span className="widget-error__icon">⚠️</span>
                        <p className="widget-error__message">{error}</p>
                        {onRetry && (
                            <button className="widget-error__retry" onClick={onRetry}>
                                {t('widgets.retry')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`widget widget--${type} widget--${size} ${className}`} {...props}>
            {(title || subtitle || headerAction) && (
                <div className="widget__header">
                    <div className="widget__header-text">
                        {title && <h3 className="widget__title">{title}</h3>}
                        {subtitle && <span className="widget__subtitle">{subtitle}</span>}
                    </div>
                    {headerAction && <div className="widget__header-action">{headerAction}</div>}
                </div>
            )}
            <div className="widget__content">
                {children}
            </div>
        </div>
    );
};

const WidgetSkeleton = ({ type }) => {
    switch (type) {
        case 'schedule':
            return (
                <div className="widget-skeleton widget-skeleton--schedule">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="widget-skeleton__item">
                            <div className="widget-skeleton__line widget-skeleton__line--short"></div>
                            <div className="widget-skeleton__line widget-skeleton__line--medium"></div>
                        </div>
                    ))}
                </div>
            );
        case 'slots':
            return (
                <div className="widget-skeleton widget-skeleton--slots">
                    {[1, 2].map(i => (
                        <div key={i} className="widget-skeleton__slot">
                            <div className="widget-skeleton__line widget-skeleton__line--time"></div>
                            <div className="widget-skeleton__button"></div>
                        </div>
                    ))}
                </div>
            );
        case 'stat':
            return (
                <div className="widget-skeleton widget-skeleton--stat">
                    <div className="widget-skeleton__number"></div>
                    <div className="widget-skeleton__line widget-skeleton__line--medium"></div>
                    <div className="widget-skeleton__button widget-skeleton__button--full"></div>
                </div>
            );
        default:
            return (
                <div className="widget-skeleton">
                    <div className="widget-skeleton__line"></div>
                    <div className="widget-skeleton__line widget-skeleton__line--medium"></div>
                </div>
            );
    }
};

const ScheduleItem = ({ 
    name, 
    time, 
    label, 
    status = 'default',
    onClick,
    avatar
}) => {
    return (
        <div 
            className={`widget-schedule-item widget-schedule-item--${status}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
        >
            <div className="widget-schedule-item__left">
                {avatar && <div className="widget-schedule-item__avatar">{avatar}</div>}
                <span className="widget-schedule-item__name">{name}</span>
                <span className="widget-schedule-item__time">{time}</span>
            </div>
            <div className="widget-schedule-item__right">
                <span className="widget-schedule-item__label">{label}</span>
            </div>
        </div>
    );
};

const SlotItem = ({
    time,
    buttonText,
    onClick,
    disabled = false,
    variant = 'primary'
}) => {
    const { t } = useTranslation('common');
    return (
        <div className="widget-slot-item">
            <span className="widget-slot-item__time">{time}</span>
            <button
                className={`widget-slot-item__button widget-slot-item__button--${variant}`}
                onClick={onClick}
                disabled={disabled}
            >
                {buttonText || t('widgets.bookTraining')}
            </button>
        </div>
    );
};

const Stat = ({ 
    value, 
    label, 
    prefix = '',
    suffix = '',
    trend,
    size = 'large'
}) => {
    return (
        <div className={`widget-stat widget-stat--${size}`}>
            <div className="widget-stat__value">
                {prefix && <span className="widget-stat__prefix">{prefix}</span>}
                <span className="widget-stat__number">{value}</span>
                {suffix && <span className="widget-stat__suffix">{suffix}</span>}
            </div>
            {label && <p className="widget-stat__label">{label}</p>}
            {trend && (
                <div className={`widget-stat__trend widget-stat__trend--${trend.direction}`}>
                    <span className="widget-stat__trend-icon">
                        {trend.direction === 'up' ? '↑' : '↓'}
                    </span>
                    <span className="widget-stat__trend-value">{trend.value}%</span>
                </div>
            )}
        </div>
    );
};

const Button = ({ 
    children, 
    onClick, 
    variant = 'primary',
    size = 'medium',
    fullWidth = false,
    disabled = false,
    icon,
    ...props
}) => {
    return (
        <button 
            className={`widget-button widget-button--${variant} widget-button--${size} ${fullWidth ? 'widget-button--full' : ''}`}
            onClick={onClick}
            disabled={disabled}
            {...props}
        >
            {icon && <span className="widget-button__icon">{icon}</span>}
            <span>{children}</span>
        </button>
    );
};

const EmptyState = ({
    message,
    icon,
    action
}) => {
    const { t } = useTranslation('common');
    return (
        <div className="widget-empty">
            {icon && <div className="widget-empty__icon">{icon}</div>}
            <p className="widget-empty__message">{message || t('widgets.noData')}</p>
            {action && <div className="widget-empty__action">{action}</div>}
        </div>
    );
};

const Progress = ({ 
    value, 
    max = 100, 
    label,
    showValue = true,
    variant = 'primary',
    size = 'medium'
}) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
    
    return (
        <div className={`widget-progress widget-progress--${variant} widget-progress--${size}`}>
            {(label || showValue) && (
                <div className="widget-progress__header">
                    {label && <span className="widget-progress__label">{label}</span>}
                    {showValue && <span className="widget-progress__value">{Math.round(percentage)}%</span>}
                </div>
            )}
            <div className="widget-progress__track">
                <div 
                    className="widget-progress__fill"
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>
        </div>
    );
};

const ListItem = ({ 
    primary, 
    secondary, 
    icon,
    action,
    onClick,
    status
}) => {
    return (
        <div 
            className={`widget-list-item ${status ? `widget-list-item--${status}` : ''} ${onClick ? 'widget-list-item--clickable' : ''}`}
            onClick={onClick}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
        >
            {icon && <div className="widget-list-item__icon">{icon}</div>}
            <div className="widget-list-item__content">
                <span className="widget-list-item__primary">{primary}</span>
                {secondary && <span className="widget-list-item__secondary">{secondary}</span>}
            </div>
            {action && <div className="widget-list-item__action">{action}</div>}
        </div>
    );
};

const Alert = ({ 
    type = 'info',
    title,
    message,
    action,
    onDismiss
}) => {
    return (
        <div className={`widget-alert widget-alert--${type}`}>
            <div className="widget-alert__icon">
                {type === 'success' && '✓'}
                {type === 'warning' && '⚠'}
                {type === 'error' && '✕'}
                {type === 'info' && 'ℹ'}
            </div>
            <div className="widget-alert__content">
                {title && <strong className="widget-alert__title">{title}</strong>}
                {message && <p className="widget-alert__message">{message}</p>}
            </div>
            {action && <div className="widget-alert__action">{action}</div>}
            {onDismiss && (
                <button className="widget-alert__dismiss" onClick={onDismiss}>×</button>
            )}
        </div>
    );
};

const QuickActions = ({ actions = [] }) => {
    return (
        <div className="widget-quick-actions">
            {actions.map((action, index) => (
                <button
                    key={index}
                    className={`widget-quick-action ${action.variant ? `widget-quick-action--${action.variant}` : ''}`}
                    onClick={action.onClick}
                    disabled={action.disabled}
                >
                    {action.icon && <span className="widget-quick-action__icon">{action.icon}</span>}
                    <span className="widget-quick-action__label">{action.label}</span>
                </button>
            ))}
        </div>
    );
};

const Badge = ({ children, variant = 'default', size = 'medium' }) => {
    return (
        <span className={`widget-badge widget-badge--${variant} widget-badge--${size}`}>
            {children}
        </span>
    );
};

const Divider = ({ text }) => {
    return (
        <div className="widget-divider">
            {text && <span className="widget-divider__text">{text}</span>}
        </div>
    );
};

const Counter = ({ 
    value, 
    onIncrement, 
    onDecrement, 
    min = 0, 
    max = 99,
    label
}) => {
    return (
        <div className="widget-counter">
            {label && <span className="widget-counter__label">{label}</span>}
            <div className="widget-counter__controls">
                <button 
                    className="widget-counter__btn"
                    onClick={onDecrement}
                    disabled={value <= min}
                >
                    −
                </button>
                <span className="widget-counter__value">{value}</span>
                <button 
                    className="widget-counter__btn"
                    onClick={onIncrement}
                    disabled={value >= max}
                >
                    +
                </button>
            </div>
        </div>
    );
};

const Timeline = ({ items = [] }) => {
    return (
        <div className="widget-timeline">
            {items.map((item, index) => (
                <div key={index} className={`widget-timeline__item ${item.active ? 'widget-timeline__item--active' : ''}`}>
                    <div className="widget-timeline__marker"></div>
                    <div className="widget-timeline__content">
                        <span className="widget-timeline__time">{item.time}</span>
                        <span className="widget-timeline__text">{item.text}</span>
                    </div>
                </div>
            ))}
        </div>
    );
};

const Avatar = ({ src, name, size = 'medium' }) => {
    const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
    
    return (
        <div className={`widget-avatar widget-avatar--${size}`}>
            {src ? (
                <img src={src} alt={name} className="widget-avatar__image" />
            ) : (
                <span className="widget-avatar__initials">{initials}</span>
            )}
        </div>
    );
};

const InfoRow = ({ label, value, icon }) => {
    return (
        <div className="widget-info-row">
            {icon && <span className="widget-info-row__icon">{icon}</span>}
            <span className="widget-info-row__label">{label}</span>
            <span className="widget-info-row__value">{value}</span>
        </div>
    );
};

const Rating = ({ value, max = 5, onChange, readonly = true }) => {
    return (
        <div className={`widget-rating ${readonly ? 'widget-rating--readonly' : ''}`}>
            {[...Array(max)].map((_, i) => (
                <span
                    key={i}
                    className={`widget-rating__star ${i < value ? 'widget-rating__star--filled' : ''}`}
                    onClick={!readonly ? () => onChange(i + 1) : undefined}
                >
                    ★
                </span>
            ))}
        </div>
    );
};

const Tag = ({ children, variant = 'default', onRemove }) => {
    return (
        <span className={`widget-tag widget-tag--${variant}`}>
            {children}
            {onRemove && (
                <button className="widget-tag__remove" onClick={onRemove}>×</button>
            )}
        </span>
    );
};

const MiniCalendar = ({ selectedDate, onDateSelect, events = [] }) => {
    const { t, i18n } = useTranslation('common');
    const today = new Date();
    const currentMonth = selectedDate || today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days = [];
    for (let i = 0; i < startPadding; i++) {
        days.push(null);
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push(i);
    }

    const hasEvent = (day) => {
        if (!day) return false;
        return events.some(e => {
            const eventDate = new Date(e);
            return eventDate.getDate() === day && 
                   eventDate.getMonth() === currentMonth.getMonth() &&
                   eventDate.getFullYear() === currentMonth.getFullYear();
        });
    };

    const isToday = (day) => {
        return day === today.getDate() && 
               currentMonth.getMonth() === today.getMonth() &&
               currentMonth.getFullYear() === today.getFullYear();
    };

    return (
        <div className="widget-mini-calendar">
            <div className="widget-mini-calendar__header">
                {currentMonth.toLocaleDateString(i18n.language === 'pl' ? 'pl-PL' : 'en-US', { month: 'long', year: 'numeric' })}
            </div>
            <div className="widget-mini-calendar__weekdays">
                {(t('widgets.weekdaysShort', { returnObjects: true }) || ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd']).map(d => (
                    <span key={d} className="widget-mini-calendar__weekday">{d}</span>
                ))}
            </div>
            <div className="widget-mini-calendar__days">
                {days.map((day, i) => (
                    <span 
                        key={i} 
                        className={`widget-mini-calendar__day ${!day ? 'widget-mini-calendar__day--empty' : ''} ${isToday(day) ? 'widget-mini-calendar__day--today' : ''} ${hasEvent(day) ? 'widget-mini-calendar__day--has-event' : ''}`}
                        onClick={day && onDateSelect ? () => onDateSelect(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)) : undefined}
                    >
                        {day}
                    </span>
                ))}
            </div>
        </div>
    );
};

Widget.ScheduleItem = ScheduleItem;
Widget.SlotItem = SlotItem;
Widget.Stat = Stat;
Widget.Button = Button;
Widget.EmptyState = EmptyState;
Widget.Progress = Progress;
Widget.ListItem = ListItem;
Widget.Alert = Alert;
Widget.QuickActions = QuickActions;
Widget.Badge = Badge;
Widget.Divider = Divider;
Widget.Counter = Counter;
Widget.Timeline = Timeline;
Widget.Avatar = Avatar;
Widget.InfoRow = InfoRow;
Widget.Rating = Rating;
Widget.Tag = Tag;
Widget.MiniCalendar = MiniCalendar;
Widget.Skeleton = WidgetSkeleton;

export default Widget;
